import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { StockService } from '../../../../services/stock.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import { StatusService } from '../../../../services/status.service';
import { Stock } from '../../../../models/Stock';
import { Warehouse } from '../../../../models/Warehouse';
import { Status } from '../../../../models/Status';

@Component({
  selector: 'app-stock-form',
  templateUrl: './stock-form.component.html',
  styleUrls: ['./stock-form.component.css']
})
export class StockFormComponent implements OnInit {
  formStep1: FormGroup;
  formStep2: FormGroup;
  formStep3: FormGroup;
  currentStep = 1;
  totalSteps = 3;
  availableTabs = [
    { id: 'step1', label: 'Identificação', icon: 'fas fa-info-circle', required: true, completed: false },
    { id: 'step2', label: 'Localização e Quantidades', icon: 'fas fa-map-marker-alt', required: false, completed: false },
    { id: 'step3', label: 'Valores e Configurações', icon: 'fas fa-dollar-sign', required: false, completed: false }
  ];
  activeTab = 'step1';
  stockId?: number;
  isSubmitting = false;
  warehouses: Warehouse[] = [];

  // Opções para selects
  stockTypes = [
    'Peça de Moto',
    'Acessório de Segurança',
    'Ferramenta',
    'Produto de Limpeza',
    'Equipamento',
    'Suprimento',
    'Documento',
    'Outros'
  ];

  stockCategories = [
    'Motor',
    'Transmissão',
    'Freios',
    'Suspensão',
    'Elétrica',
    'Pneus e Rodas',
    'Carroceria',
    'Acessórios',
    'Ferramentas',
    'Limpeza',
    'Segurança',
    'Documentação'
  ];

  statusOptions: Status[] = [];

  departments = [
    'Oficina',
    'Vendas',
    'Locação',
    'Administrativo',
    'Segurança',
    'Limpeza',
    'TI'
  ];

  branches = [
    'Matriz',
    'Filial Norte',
    'Filial Sul',
    'Filial Leste',
    'Filial Oeste'
  ];

  units = [
    'Unidade',
    'Peça',
    'Kit',
    'Conjunto',
    'Litro',
    'Quilograma',
    'Metro',
    'Metro Quadrado',
    'Metro Cúbico',
    'Caixa',
    'Pacote',
    'Rolo'
  ];

  currencyCodes = [
    'BRL',
    'USD',
    'EUR'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private statusService: StatusService,
    private navLocation: Location
  ) {
    this.formStep1 = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      type: ['Peça de Moto', Validators.required],
      category: [''],
      parentStockId: [null],
      level: [''],
      hierarchyPath: [''],
      status: ['Ativo', Validators.required]
    });

    this.formStep2 = this.fb.group({
      warehouseId: [null],
      location: ['', Validators.maxLength(200)],
      department: [''],
      branch: [''],
      unit: ['Unidade'],
      minimumQuantity: [null, [Validators.min(0)]],
      maximumQuantity: [null, [Validators.min(0)]],
      currentQuantity: [0, [Validators.min(0)]],
      availableQuantity: [0, [Validators.min(0)]],
      responsiblePerson: ['', Validators.maxLength(200)],
      responsibleEmail: ['', [Validators.email, Validators.maxLength(100)]],
      managerStaffId: [null]
    });

    this.formStep3 = this.fb.group({
      unitCost: [null, [Validators.min(0)]],
      totalValue: [null, [Validators.min(0)]],
      currencyCode: ['BRL'],
      lastInventoryDate: [''],
      nextInventoryDate: [''],
      isActive: [true],
      allowsNegativeStock: [false],
      trackBatch: [false],
      trackExpiration: [false],
      stockGroup: ['', Validators.maxLength(100)],
      notes: ['', Validators.maxLength(1000)]
    });
  }

  ngOnInit(): void {
    this.loadWarehouses();
    this.loadStatuses();
    
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.stockId = Number(idParam);
      this.loadStock();
    }

    this.formStep2.get('warehouseId')?.valueChanges.subscribe((rawWarehouseId: number | string | null) => {
      if (rawWarehouseId === null || rawWarehouseId === undefined || rawWarehouseId === '') {
        return;
      }

      const warehouseId = typeof rawWarehouseId === 'string' ? parseInt(rawWarehouseId, 10) : rawWarehouseId;
      if (!warehouseId || isNaN(warehouseId)) {
        return;
      }

      const selectedWarehouse = this.warehouses.find(warehouse => warehouse.id === warehouseId);
      if (!selectedWarehouse) {
        return;
      }

      this.formStep2.patchValue({
        department: selectedWarehouse.department || this.formStep2.get('department')?.value,
        branch: selectedWarehouse.branch || this.formStep2.get('branch')?.value,
        managerStaffId: selectedWarehouse.managerStaffId || this.formStep2.get('managerStaffId')?.value,
        responsiblePerson: selectedWarehouse.responsiblePerson || this.formStep2.get('responsiblePerson')?.value,
        responsibleEmail: selectedWarehouse.responsibleEmail || this.formStep2.get('responsibleEmail')?.value
      }, { emitEvent: false });
    });

    this.syncTabFromStep();
    this.updateTabsCompletionStatus();
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: result => {
        this.warehouses = result.items || [];
      },
      error: _ => {
        this.warehouses = [];
      }
    });
  }

  loadStatuses(): void {
    this.statusService.getByEntity('Stock').subscribe({
      next: (statuses) => {
        this.statusOptions = statuses;
      },
      error: (err) => {
        console.error('Erro ao carregar status:', err);
        this.statusOptions = [];
      }
    });
  }

  loadStock(): void {
    if (!this.stockId) return;
    
    this.stockService.getById(this.stockId).subscribe({
      next: stock => {
        this.formStep1.patchValue({
          code: stock.code,
          name: stock.name,
          description: stock.description,
          type: stock.type,
          category: stock.category,
          parentStockId: stock.parentStockId,
          level: stock.level,
          hierarchyPath: stock.hierarchyPath,
          status: stock.status
        });

        this.formStep2.patchValue({
          warehouseId: stock.warehouseId,
          location: stock.location,
          department: stock.department,
          branch: stock.branch,
          unit: stock.unit,
          minimumQuantity: stock.minimumQuantity,
          maximumQuantity: stock.maximumQuantity,
          currentQuantity: stock.currentQuantity,
          availableQuantity: stock.availableQuantity,
          responsiblePerson: stock.responsiblePerson,
          responsibleEmail: stock.responsibleEmail,
          managerStaffId: stock.managerStaffId
        });

        this.formStep3.patchValue({
          unitCost: stock.unitCost,
          totalValue: stock.totalValue,
          currencyCode: stock.currencyCode,
          lastInventoryDate: stock.lastInventoryDate ? stock.lastInventoryDate.substring(0, 10) : '',
          nextInventoryDate: stock.nextInventoryDate ? stock.nextInventoryDate.substring(0, 10) : '',
          isActive: stock.isActive,
          allowsNegativeStock: stock.allowsNegativeStock,
          trackBatch: stock.trackBatch,
          trackExpiration: stock.trackExpiration,
          stockGroup: stock.stockGroup,
          notes: stock.notes
        });
      },
      error: _ => {
        this.router.navigate(['/stock/inventory']);
      }
    });
  }

  nextStep(): void { 
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    }
  }
  
  prevStep(): void { 
    if (this.currentStep > 1) {
      this.currentStep--;
      this.syncTabFromStep();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.syncStepFromTab();
  }

  save(): void {
    const isStep1Valid = this.formStep1.valid;
    const isStep2Valid = this.formStep2.valid;
    const isStep3Valid = this.formStep3.valid;
    
    if (!isStep1Valid || !isStep2Valid || !isStep3Valid) {
      this.markFormGroupTouched();
      return;
    }

    this.isSubmitting = true;
    const payload = { 
      ...this.formStep1.value, 
      ...this.formStep2.value, 
      ...this.formStep3.value 
    } as any;

    // Converter datas para ISO string
    if (payload.lastInventoryDate) {
      payload.lastInventoryDate = new Date(payload.lastInventoryDate).toISOString();
    }
    if (payload.nextInventoryDate) {
      payload.nextInventoryDate = new Date(payload.nextInventoryDate).toISOString();
    }

    if (this.stockId) {
      this.stockService.update(this.stockId, payload).subscribe({
        next: _ => {
          this.isSubmitting = false;
          this.router.navigate(['/stock/inventory']);
        },
        error: _ => {
          this.isSubmitting = false;
        }
      });
    } else {
      this.stockService.create(payload).subscribe({
        next: _ => {
          this.isSubmitting = false;
          this.router.navigate(['/stock/inventory']);
        },
        error: _ => {
          this.isSubmitting = false;
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/stock/inventory']);
  }

  goBack(): void {
    try {
      if (window.history.length > 1) {
        this.navLocation.back();
        return;
      }
    } catch (_) { /* noop */ }
    this.router.navigate(['/stock/inventory']);
  }

  markFormGroupTouched(): void {
    Object.keys(this.formStep1.controls).forEach(key => {
      const control = this.formStep1.get(key);
      control?.markAsTouched();
    });
    Object.keys(this.formStep2.controls).forEach(key => {
      const control = this.formStep2.get(key);
      control?.markAsTouched();
    });
    Object.keys(this.formStep3.controls).forEach(key => {
      const control = this.formStep3.get(key);
      control?.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const formGroup = this.getFormGroupForField(fieldName);
    const field = formGroup?.get(fieldName);
    return !!(field?.invalid && field?.touched);
  }

  getFieldError(fieldName: string): string {
    const formGroup = this.getFormGroupForField(fieldName);
    const field = formGroup?.get(fieldName);
    
    if (!field?.errors) return '';
    
    if (field.errors['required']) return 'Campo obrigatório';
    if (field.errors['minlength']) return `Mínimo de ${field.errors['minlength'].requiredLength} caracteres`;
    if (field.errors['maxlength']) return `Máximo de ${field.errors['maxlength'].requiredLength} caracteres`;
    if (field.errors['email']) return 'Email inválido';
    if (field.errors['min']) return `Valor mínimo é ${field.errors['min'].min}`;
    
    return 'Campo inválido';
  }

  private getFormGroupForField(fieldName: string): FormGroup | null {
    if (this.formStep1.contains(fieldName)) return this.formStep1;
    if (this.formStep2.contains(fieldName)) return this.formStep2;
    if (this.formStep3.contains(fieldName)) return this.formStep3;
    return null;
  }

  // Getters para facilitar o acesso no template
  get code() { return this.formStep1.get('code'); }
  get name() { return this.formStep1.get('name'); }
  get description() { return this.formStep1.get('description'); }
  get type() { return this.formStep1.get('type'); }
  get category() { return this.formStep1.get('category'); }
  get status() { return this.formStep1.get('status'); }
  
  get warehouseId() { return this.formStep2.get('warehouseId'); }
  get location() { return this.formStep2.get('location'); }
  get unit() { return this.formStep2.get('unit'); }
  get minimumQuantity() { return this.formStep2.get('minimumQuantity'); }
  get maximumQuantity() { return this.formStep2.get('maximumQuantity'); }
  get currentQuantity() { return this.formStep2.get('currentQuantity'); }
  get responsiblePerson() { return this.formStep2.get('responsiblePerson'); }
  get responsibleEmail() { return this.formStep2.get('responsibleEmail'); }
  
  get unitCost() { return this.formStep3.get('unitCost'); }
  get totalValue() { return this.formStep3.get('totalValue'); }
  get notes() { return this.formStep3.get('notes'); }

  private syncTabFromStep(): void {
    this.activeTab = `step${this.currentStep}`;
  }

  private syncStepFromTab(): void {
    const parsed = Number(this.activeTab.replace('step', ''));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= this.totalSteps) {
      this.currentStep = parsed;
    }
  }

  private updateTabsCompletionStatus(): void {
    this.availableTabs.forEach((tab, index) => {
      tab.completed = this.currentStep > index + 1;
    });
  }
}
